import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { productDraftApiService } from "../../../../service/admin/productDraftApiService";
import { useLoader } from "../../../../base/context/LoaderProvider";
import {
  ToastTypes,
  useMessage,
} from "../../../../base/context/MessageProvider";
import WebButton from "../../../../component/library/webButton";
import "./style.css";
import AlibabaImage from "../../../../component/common/AlibabaImage";

export default function ViewProductDraft() {
  const { id } = useParams();
  const navigate = useNavigate();
  const loader = useLoader();
  const message = useMessage();
  
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    fetchDraftData();
  }, [id]);

  const fetchDraftData = () => {
    loader.showLoading(true);
    productDraftApiService
      .getProductDraftById(id)
      .then((res) => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          setDraft(res.data.draft);
        } else if (res.status.code === 402) {
          navigate("/login");
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
        loader.showLoading(false);
      });
  };

  // Normalize description HTML for production so images load
  const transformDescriptionForProd = (html) => {
    if (!html) return "";
    let safe = String(html);
    // Normalize http -> https for image src
    safe = safe.replace(/(<img\b[^>]*?src\s*=\s*")(http:)([^"]+)("[^>]*>)/gi, ($0, p1, _p2, p3, p4) => `${p1}https:${p3}${p4}`);
    // Add referrerpolicy and crossorigin attributes to img tags
    safe = safe.replace(/<img\b([^>]*)>/gi, (m, attrs) => {
      let a = attrs;
      if (!/referrerpolicy=/i.test(a)) a += ' referrerpolicy="no-referrer"';
      if (!/crossorigin=/i.test(a)) a += ' crossorigin="anonymous"';
      return `<img${a}>`;
    });
    return safe;
  };

  const handleEdit = () => {
    navigate(`/admin/product-drafts/edit/${id}`);
  };

  const handleBack = () => {
    navigate("/admin/product-drafts");
  };



  if (!draft) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="view-draft-container">
      <div className="view-draft-header">
        <h1>Product Draft Details</h1>
        <p>View product draft information</p>
      </div>

      <div className="view-draft-content">
        <div className="draft-grid">
          {/* Basic Information */}
          <div className="draft-section">
            <h2>Basic Information</h2>
            
            <div className="info-grid">
              <div className="info-item">
                <label>Product Name:</label>
                <span>{draft.item_name || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>SKU:</label>
                <span>{draft.item_sku || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Brand Name:</label>
                <span>{draft.brand_name || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Manufacturer:</label>
                <span>{draft.manufacturer || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Model:</label>
                <span>{draft.model || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Part Number:</label>
                <span>{draft.part_number || "N/A"}</span>
              </div>
            </div>

            <div className="info-item full-width">
              <label>Product Description:</label>
              <div
                className="product-description-html"
                dangerouslySetInnerHTML={{
                  __html: transformDescriptionForProd(draft.description || draft.product_description || ""),
                }}
              />
            </div>
          </div>

          {/* Pricing & Inventory */}
          <div className="draft-section">
            <h2>Pricing & Inventory</h2>
            
            <div className="info-grid">
              <div className="info-item">
                <label>Standard Price:</label>
                <span>${draft.standard_price || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Sale Price:</label>
                <span>${draft.sale_price || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Quantity:</label>
                <span>{draft.quantity || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Max Order Quantity:</label>
                <span>{draft.max_order_quantity || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Currency:</label>
                <span>{draft.currency || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Number of Items:</label>
                <span>{draft.number_of_items || "N/A"}</span>
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="draft-section">
            <h2>Images</h2>
            
            <div className="image-grid">
              {draft.main_image_url && (
                <div className="image-item">
                  <label>Main Image:</label>
                  <AlibabaImage 
                    src={draft.main_image_url} 
                    alt="Main" 
                    width="150px"
                    height="150px"
                    className="medium"
                  />
                </div>
              )}
              
              {draft.other_image_url1 && (
                <div className="image-item">
                  <label>Image 1:</label>
                  <AlibabaImage 
                    src={draft.other_image_url1} 
                    alt="Image 1" 
                    width="120px"
                    height="120px"
                    className="medium"
                  />
                </div>
              )}
              
              {draft.other_image_url2 && (
                <div className="image-item">
                  <label>Image 2:</label>
                  <AlibabaImage 
                    src={draft.other_image_url2} 
                    alt="Image 2" 
                    width="120px"
                    height="120px"
                    className="medium"
                  />
                </div>
              )}
              
              {draft.other_image_url3 && (
                <div className="image-item">
                  <label>Image 3:</label>
                  <AlibabaImage 
                    src={draft.other_image_url3} 
                    alt="Image 3" 
                    width="120px"
                    height="120px"
                    className="medium"
                  />
                </div>
              )}
            </div>
          </div>

          {/* SEO & Keywords */}
          <div className="draft-section">
            <h2>SEO & Keywords</h2>
            
            <div className="info-grid">
              <div className="info-item full-width">
                <label>Generic Keywords:</label>
                <span>{draft.generic_keywords || "N/A"}</span>
              </div>
              <div className="info-item full-width">
                <label>Recommended Browse Nodes:</label>
                <span>{draft.recommended_browse_nodes || "N/A"}</span>
              </div>
            </div>

            <div className="bullet-points">
              <h3>Bullet Points:</h3>
              <ul>
                {draft.bullet_point1 && <li>{draft.bullet_point1}</li>}
                {draft.bullet_point2 && <li>{draft.bullet_point2}</li>}
                {draft.bullet_point3 && <li>{draft.bullet_point3}</li>}
                {draft.bullet_point4 && <li>{draft.bullet_point4}</li>}
                {draft.bullet_point5 && <li>{draft.bullet_point5}</li>}
              </ul>
            </div>
          </div>

          {/* Shipping & Condition */}
          <div className="draft-section">
            <h2>Shipping & Condition</h2>
            
            <div className="info-grid">
              <div className="info-item">
                <label>Fulfillment Latency:</label>
                <span>{draft.fulfillment_latency ? `${draft.fulfillment_latency} days` : "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Condition Type:</label>
                <span>{draft.condition_type || "N/A"}</span>
              </div>
              <div className="info-item full-width">
                <label>Condition Note:</label>
                <span>{draft.condition_note || "N/A"}</span>
              </div>
            </div>

            <div className="gift-options">
              <h3>Gift Options:</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Can be gift wrapped:</label>
                  <span>{draft.offering_can_be_giftwrapped ? "Yes" : "No"}</span>
                </div>
                <div className="info-item">
                  <label>Can be gift messaged:</label>
                  <span>{draft.offering_can_be_gift_messaged ? "Yes" : "No"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Status & Metadata */}
          <div className="draft-section">
            <h2>Status & Metadata</h2>
            
            <div className="info-grid">
              <div className="info-item">
                <label>Processing Status:</label>
                <span className={`status-badge ${draft.processingStatus}`}>
                  {draft.processingStatus}
                </span>
              </div>
              <div className="info-item">
                <label>Source:</label>
                <span>{draft.source || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Offer ID:</label>
                <span>{draft.offerId || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Subscribers:</label>
                <span>{draft.subscribedBy?.length || 0}</span>
              </div>
              <div className="info-item">
                <label>Created At:</label>
                <span>{new Date(draft.createdAt).toLocaleString()}</span>
              </div>
              <div className="info-item">
                <label>Updated At:</label>
                <span>{new Date(draft.updatedAt).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* AI Generated Content */}
          {draft.aiGenerated && (
            <div className="draft-section">
              <h2>AI Generated Content</h2>
              
              <div className="info-grid">
                <div className="info-item full-width">
                  <label>AI Generated Title:</label>
                  <span>{draft.aiGenerated.title || "N/A"}</span>
                </div>
                <div className="info-item full-width">
                  <label>AI Generated Description:</label>
                  <span>{draft.aiGenerated.description || "N/A"}</span>
                </div>
                <div className="info-item full-width">
                  <label>AI Generated Keywords:</label>
                  <span>{draft.aiGenerated.keywords || "N/A"}</span>
                </div>
                <div className="info-item">
                  <label>Generated At:</label>
                  <span>{draft.aiGenerated.generatedAt ? new Date(draft.aiGenerated.generatedAt).toLocaleString() : "N/A"}</span>
                </div>
              </div>

              {draft.aiGenerated.bulletPoints && draft.aiGenerated.bulletPoints.length > 0 && (
                <div className="bullet-points">
                  <h3>AI Generated Bullet Points:</h3>
                  <ul>
                    {draft.aiGenerated.bulletPoints.map((point, index) => (
                      <li key={index}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="draft-actions">
          <WebButton
            type="button"
            text="Back to List"
            onClick={handleBack}
            className="back-button"
          />
          <WebButton
            type="button"
            text="Edit Draft"
            onClick={handleEdit}
            className="edit-button"
          />
        </div>
      </div>
    </div>
  );
}
